import {
  SectionVisibilityDefaultsSchema,
  type SectionVisibilityDefaultsRequest,
} from '@aysar/contracts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CancelledSubmit } from '@/shared/components/dynamic-form/cancelled-submit/cancelled-submit';
import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useConfirm } from '@/shared/hooks/useConfirm/useConfirm';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import { useApplySectionVisibilityDefaultsMutation } from '../../services/profile-questions/profile-questions.api';
import { useSectionDetailQuery } from '../../services/question-sections/question-sections.api';
import { buildSectionVisibilityDefaultsFields } from '../../config/section-visibility-defaults.config';

export interface UseSectionVisibilityDefaultsModalLogicInput {
  sectionId: string;
  onDone: () => void;
}

export interface UseSectionVisibilityDefaultsModalLogic {
  title: string;
  /** What the write reaches, in the operator's own words, with the real count. */
  scope: string;
  submitLabel: string;
  /** How many questions the write will cover — the section's own number. */
  questionCount: number;
  /**
   * The section could not be read, so the scope is a sentence rather than a
   * number — and the write refuses until it is one.
   */
  isCountUnknown: boolean;
  /** Reads the section again after a failed attempt. */
  retryCount: () => void;
  retryLabel: string;
  /** Held until the section arrives: RHF reads `defaultValues` exactly once. */
  isLoading: boolean;
  fields: readonly FieldDef[];
  schema: typeof SectionVisibilityDefaultsSchema;
  initialValues: Partial<SectionVisibilityDefaultsRequest>;
  submit: (values: SectionVisibilityDefaultsRequest) => Promise<void>;
}

/**
 * Applying visibility defaults to a whole section (U5: the modal is
 * presentation only).
 *
 * **The count comes from the SECTION, not from the list.** The pane's
 * `meta.total` is filtered and paged, and this write reaches every non-deleted
 * question of the section — which is exactly what `QuestionSectionDetail`'s
 * `questionCount` counts, and exactly what the api reports back as `matched`. A
 * confirmation naming the filtered number would understate what it is about to
 * do, on the one write in the builder that cannot be undone.
 */
export function useSectionVisibilityDefaultsModalLogic({
  sectionId,
  onDone,
}: UseSectionVisibilityDefaultsModalLogicInput): UseSectionVisibilityDefaultsModalLogic {
  const { t } = useTranslation('questions');
  const toast = useToast();
  const { confirm } = useConfirm();

  const detail = useSectionDetailQuery(sectionId, {
    skip: sectionId === '',
    // The dialog opens rarely and a CURRENT count is its entire justification:
    // this query is also the editor's, so a question created since that screen
    // last read the section would otherwise be written by a confirmation that
    // never counted it. Nothing else in this app re-fetches on mount, and
    // nothing invalidates the `Section` tag from the questions api — the two
    // live in different slices.
    refetchOnMountOrArgChange: true,
  });
  const [applyDefaults] = useApplySectionVisibilityDefaultsMutation();

  /**
   * A failed read is **not** an empty section.
   *
   * `?? 0` alone rendered "there is nothing in this section to change" over a
   * section holding twenty-six questions the write would have reached anyway —
   * a confidently wrong number in front of an irreversible operation, which is
   * worse than the loading state the dialog already handles.
   *
   * `isError` counts too, even with a cached row still in hand: that number was
   * true when it was read, and the refetch this dialog insists on is the only
   * thing that says it still is. For a write with no undo, "I could not confirm
   * the scope" is a refusal, not a footnote.
   */
  const isCountUnknown = !detail.isLoading && (detail.data === undefined || detail.isError);
  const questionCount = detail.data?.questionCount ?? 0;

  const submit = useCallback(
    async (values: SectionVisibilityDefaultsRequest) => {
      if (isCountUnknown) {
        // Shaped as an `ApiError` rather than thrown bare, so the form renders a
        // translated sentence instead of an internal token (U8) — the
        // `AiRateEditorModal` precedent. `status: 0` is the literal truth: the
        // request never left the browser.
        const refusal: ApiError = {
          status: 0,
          code: 'section_count_unknown',
          message: t('sections.visibilityDefaults.scopeUnknownRefusal'),
        };
        throw refusal;
      }

      const accepted = await confirm({
        title: t('sections.visibilityDefaults.confirmTitle'),
        description: t('sections.visibilityDefaults.confirmMessage', { count: questionCount }),
        confirmLabel: t('sections.visibilityDefaults.confirm'),
        // Danger, not warning: this changes what is ALREADY exposed about
        // members who answered long ago, and nothing puts it back.
        tone: 'danger',
      });

      // The sentinel, not a bare Error: a declined confirmation prints nothing,
      // where any other rejection is mapped onto the form as a refusal.
      if (!accepted) throw new CancelledSubmit();

      try {
        const result = await applyDefaults({ sectionId, body: values }).unwrap();

        // `matched` and `changedIds` are DIFFERENT numbers and the toast says
        // both: the api writes only the rows that actually differed, so
        // implying every matched question changed would be a lie the operator
        // would only discover in the audit trail.
        toast.success(
          t('sections.visibilityDefaults.result', {
            count: result.changedIds.length,
            selected: result.matched,
          }),
        );
        onDone();
      } catch (error) {
        // Rethrown as an `ApiError` so the form renders the server's own message
        // — it names the section the api could not find, which our own wording
        // would drop.
        throw toApiError(error);
      }
    },
    [applyDefaults, confirm, isCountUnknown, onDone, questionCount, sectionId, t, toast],
  );

  const retryCount = useCallback(() => {
    void detail.refetch();
  }, [detail]);

  // Read out of the query result BEFORE the memo, so the dependency is the
  // string the heading actually uses rather than the query object it came from.
  // Depending on `detail.data?.title` while the body reads `detail.data` is what
  // `react-hooks/exhaustive-deps` reports — and it is right: the two are not the
  // same dependency.
  const sectionTitle = detail.data === undefined ? undefined : detail.data.title;

  return useMemo(
    () => ({
      // Naming the section is the point of this heading, so when the section
      // could not be read it says "this section" rather than printing a pair of
      // empty quotation marks (caught by the unknown-count still).
      title:
        sectionTitle === undefined
          ? t('sections.visibilityDefaults.titleUnknown')
          : t('sections.visibilityDefaults.title', { title: sectionTitle }),
      scope: isCountUnknown
        ? t('sections.visibilityDefaults.scopeUnknown')
        : t('sections.visibilityDefaults.scope', { count: questionCount }),
      submitLabel: t('sections.visibilityDefaults.submit'),
      questionCount,
      isCountUnknown,
      retryCount,
      retryLabel: t('sections.visibilityDefaults.scopeRetry'),
      isLoading: detail.isLoading,
      fields: buildSectionVisibilityDefaultsFields(t),
      schema: SectionVisibilityDefaultsSchema,
      // An EMPTY patch, deliberately: nothing is named until the operator names
      // it, so the schema's own "name at least one part" refusal is what stands
      // between a habitual press and four rewritten columns.
      initialValues: { visibility: {} },
      submit,
    }),
    [sectionTitle, detail.isLoading, isCountUnknown, questionCount, retryCount, submit, t],
  );
}
